<template>
  <div class="animated-background">
    <div class="floating-elements">
      <span class="element code">{ }</span>
      <span class="element book">📚</span>
      <span class="element graduation">🎓</span>
      <span class="element tech">💻</span>
      <span class="element atom">⚛️</span>
      <span class="element formula">∑</span>
      <span class="element infinity">∞</span>
      <span class="element binary">01</span>
      <span class="element circuit"></span>
      <span class="element grid"></span>
    </div>
  </div>

  <v-container fluid class="reports-page rtl px-0 pb-10">
    <div class="reports-hero report-section">
      <div>
        <h1 class="text-h4 font-weight-bold mb-1">لوحة التحليلات والتقارير</h1>
        <p class="text-medium-emphasis text-body-2 mb-0">
          نظرة مرئية موحدة لحجوزات القاعات والدورات ومساحات الدراسة مع تقارير قابلة للتخصيص
        </p>
      </div>
      <div class="d-flex flex-wrap align-center report-hero-actions">
        <v-select
          v-model="reportType"
          :items="reportTypes"
          item-title="text"
          item-value="value"
          label="تجميع التقرير"
          density="comfortable"
          variant="outlined"
          hide-details
          style="max-width: 220px"
          class="rtl-input hero-select"
        />
        <v-btn color="primary" :loading="refreshingAll" class="rounded-lg" prepend-icon="mdi-refresh" @click="refreshAll">
          تحديث الكل
        </v-btn>
        <v-btn variant="tonal" color="primary" class="rounded-lg" prepend-icon="mdi-download" @click="exportCsvCurrentTab">
          تصدير CSV
        </v-btn>
      </div>
    </div>

    <v-card elevation="2" rounded="xl" class="glass-card filters-card border report-card-pad report-section">
      <v-row dense align="center">
        <v-col cols="12" md="3">
          <VueDatePicker
            v-model="customDateFrom"
            model-type="format"
            locale="ar"
            format="yyyy-MM-dd"
            text-input
            teleport
            placeholder="تاريخ البداية (اختياري)"
            class="ar-date"
          />
        </v-col>
        <v-col cols="12" md="3">
          <VueDatePicker
            v-model="customDateTo"
            model-type="format"
            locale="ar"
            format="yyyy-MM-dd"
            text-input
            teleport
            placeholder="تاريخ النهاية (اختياري)"
            class="ar-date"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-switch
            v-model="useCustomDates"
            color="primary"
            hide-details
            inset
            density="compact"
            label="تطبيق تقرير مخصص حسب مدى التاريخ (على البيانات المحمّلة)"
          />
          <span class="text-caption text-medium-emphasis d-block mt-1">
            عند تعيين البداية والنهاية وتفعيل الخيار أعلاه، تُجمّع الصفوف داخل المدى (إجمالي لكل قاعة/مساحة/دورة) وتنعكس في الجداول والرسوم والتصدير.
          </span>
        </v-col>
      </v-row>
    </v-card>

    <v-tabs v-model="activeTab" color="primary" density="comfortable" grow class="report-tabs report-section" rounded="lg">
      <v-tab value="overview" prepend-icon="mdi-view-dashboard-outline">نظرة عامة</v-tab>
      <v-tab value="rooms" prepend-icon="mdi-door-open">القاعات</v-tab>
      <v-tab value="courses" prepend-icon="mdi-school-outline">الدورات</v-tab>
      <v-tab value="spaces" prepend-icon="mdi-desk-lamp">مساحات الدراسة</v-tab>
    </v-tabs>

    <v-window v-model="activeTab">
      <!-- Overview -->
      <v-window-item value="overview">
        <v-row class="overview-grid align-stretch ga-4">
          <v-col cols="12" md="3" v-for="(kpi, i) in kpiCards" :key="i">
            <v-card elevation="3" rounded="xl" class="report-card-pad kpi-card h-100" :style="{ borderTop: `4px solid ${kpi.color}` }">
              <div class="text-caption text-medium-emphasis mb-1">{{ kpi.title }}</div>
              <div class="text-h5 font-weight-bold">{{ kpi.value }}</div>
              <div class="text-caption">{{ kpi.hint }}</div>
            </v-card>
          </v-col>

          <v-col cols="12" lg="8">
            <v-card elevation="2" rounded="xl" class="report-card-pad h-100 border chart-card">
              <div class="d-flex justify-space-between align-center report-chart-heading flex-wrap ga-2">
                <span class="text-subtitle-1 font-weight-bold">توزيع إجمالي الحجوزات (حسب المصدر)</span>
              </div>
              <div class="chart-slot">
                <Doughnut v-if="comparisonChartData.datasets[0]?.data.some((x) => x > 0)" :data="comparisonChartData" :options="doughnutOptions" />
                <div v-else class="chart-empty-state">لا توجد بيانات — اضغط «تحديث الكل»</div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" lg="4">
            <v-card elevation="2" rounded="xl" class="report-card-pad h-100 border chart-card">
              <div class="text-subtitle-1 font-weight-bold report-chart-heading">المدفوعات الإجمالية</div>
              <div class="chart-slot-sm">
                <Bar v-if="paymentsBarData.datasets[0]?.data.length" :data="paymentsBarData" :options="horizontalBarOpts" />
                <div v-else class="chart-empty-state">لا توجد بيانات بعد</div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card elevation="2" rounded="xl" class="report-card-pad border chart-card">
              <div class="text-subtitle-1 font-weight-bold report-chart-heading">اتجاهات الحجوزات عبر الزمن — مقارنة</div>
              <div class="chart-slot-wide">
                <Line v-if="trendMixedData.datasets[0]?.data.length" :data="trendMixedData" :options="lineTrendOpts" />
                <div v-else class="chart-empty-state">حمّل التقارير لعرض الاتجاه</div>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-window-item>

      <!-- Rooms -->
      <v-window-item value="rooms">
        <v-card elevation="2" rounded="xl" class="report-card-pad border report-toolbar report-section">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedRoom"
                :items="rooms"
                item-title="name"
                item-value="id"
                label="القاعة (اختياري — الكل بدون تحديد)"
                class="rtl-input"
                density="comfortable"
                variant="outlined"
                hide-details
                clearable
                return-object
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="reportType"
                :items="reportTypes"
                item-title="text"
                item-value="value"
                label="نوع التجميع"
                class="rtl-input"
                density="comfortable"
                variant="outlined"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-btn color="primary" class="rounded-lg" :loading="loadingRoom" prepend-icon="mdi-chart-box-outline" @click="generateReport">
                تحديث التقرير
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-row class="ga-4">
          <v-col cols="12" lg="7">
            <v-card elevation="2" rounded="xl" class="report-card-pad border chart-card report-chart-gap">
              <div class="text-subtitle-1 font-weight-bold report-chart-heading">صافي قيمة الحجوزات</div>
              <div class="chart-slot-tall">
                <Bar v-if="roomBarLabels.length" :data="roomBarChart" :options="verticalBarOpts" />
                <div v-else class="chart-empty-state">لا توجد بيانات</div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" lg="5">
            <v-card elevation="2" rounded="xl" class="report-card-pad border chart-card report-chart-gap">
              <div class="text-subtitle-1 font-weight-bold report-chart-heading">عدد الحجوزات</div>
              <div class="chart-slot">
                <Line v-if="roomBarLabels.length" :data="roomLineChart" :options="lineTrendOpts" />
                <div v-else class="chart-empty-state">لا توجد بيانات</div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-data-table
          v-if="displayRoomRows.length"
          :headers="getReportHeaders"
          :items="displayRoomRowsFormatted"
          class="elevated-table rtl-table elevation-2 rounded-xl report-data-table"
          :items-per-page="12"
          density="comfortable"
        />
        <v-alert v-else-if="!loadingRoom" type="info" rounded="lg" variant="tonal" class="report-table-msg">
          لا توجد بيانات للفترة / الفلاتر الحالية.
        </v-alert>
      </v-window-item>

      <!-- Courses -->
      <v-window-item value="courses">
        <v-card elevation="2" rounded="xl" class="report-card-pad border report-toolbar report-section">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select
                v-model="courseReportType"
                :items="reportTypes"
                item-title="text"
                item-value="value"
                label="نوع التجميع"
                class="rtl-input"
                density="comfortable"
                variant="outlined"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-btn color="primary" rounded="lg" :loading="courseLoading" prepend-icon="mdi-chart-multiline" @click="generateCourseReport">
                تحديث التقرير
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-row class="report-row-gap">
          <v-col cols="12" lg="6">
            <v-card elevation="2" rounded="xl" class="report-card-pad border chart-card">
              <div class="text-subtitle-1 font-weight-bold report-chart-heading">المشاركون والمدفوعات</div>
              <div class="chart-slot-tall">
                <Bar v-if="courseBarLabels.length" :data="courseGroupedChart" :options="verticalBarGroupedOpts" />
                <div v-else class="chart-empty-state">لا توجد بيانات</div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" lg="6">
            <v-card elevation="2" rounded="xl" class="report-card-pad border chart-card">
              <div class="text-subtitle-1 font-weight-bold report-chart-heading">إجمالي السعر مقابل الخصومات</div>
              <div class="chart-slot-tall">
                <Line v-if="courseBarLabels.length" :data="coursePriceTrend" :options="lineTrendOpts" />
                <div v-else class="chart-empty-state">لا توجد بيانات</div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-data-table
          v-if="displayCourseRowsFormatted.length"
          :headers="getCourseReportHeaders"
          :items="displayCourseRowsFormatted"
          class="elevated-table rtl-table elevation-2 rounded-xl report-data-table"
          :items-per-page="12"
          density="comfortable"
        />
        <v-alert v-else-if="!courseLoading" type="info" rounded="lg" variant="tonal" class="report-table-msg">
          لا توجد بيانات متاحة للفترة المحددة
        </v-alert>
      </v-window-item>

      <!-- Spaces -->
      <v-window-item value="spaces">
        <v-card elevation="2" rounded="xl" class="report-card-pad border report-toolbar report-section">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedSpace"
                :items="spaces"
                item-title="name"
                item-value="id"
                label="المساحة (اختياري)"
                class="rtl-input"
                density="comfortable"
                variant="outlined"
                hide-details
                clearable
                return-object
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-select
                v-model="spaceReportType"
                :items="reportTypes"
                item-title="text"
                item-value="value"
                label="نوع التجميع"
                class="rtl-input"
                density="comfortable"
                variant="outlined"
                hide-details
              />
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-btn color="primary" rounded="lg" :loading="spaceLoading" prepend-icon="mdi-chart-bell-curve" @click="generateSpaceReport">
                تحديث التقرير
              </v-btn>
            </v-col>
          </v-row>
          <v-alert v-if="spaceSource === 'aggregated'" type="success" density="comfortable" variant="tonal" rounded="lg" class="report-toolbar-note">
            يتم تجميع بيانات المساحات محلياً من قائمة الحجوزات (واجهة محلية عند عدم توفر تقرير خادم مخصص).
          </v-alert>
        </v-card>

        <v-row class="report-row-gap">
          <v-col cols="12" lg="7">
            <v-card elevation="2" rounded="xl" class="report-card-pad border chart-card">
              <div class="text-subtitle-1 font-weight-bold report-chart-heading">صافي قيمة الحجوزات — مساحات الدراسة</div>
              <div class="chart-slot-tall">
                <Bar v-if="spaceBarLabels.length" :data="spaceBarChart" :options="verticalBarOpts" />
                <div v-else class="chart-empty-state">لا توجد بيانات</div>
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" lg="5">
            <v-card elevation="2" rounded="xl" class="report-card-pad border chart-card">
              <div class="text-subtitle-1 font-weight-bold report-chart-heading">المدفوعات المحصلة</div>
              <div class="chart-slot">
                <Doughnut v-if="spaceByPaymentTotal > 0" :data="spacePaymentDonut" :options="doughnutOptions" />
                <div v-else class="chart-empty-state">لا توجد بيانات</div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-data-table
          v-if="displaySpaceRowsFormatted.length"
          :headers="getSpaceReportHeaders"
          :items="displaySpaceRowsFormatted"
          class="elevated-table rtl-table elevation-2 rounded-xl report-data-table"
          :items-per-page="12"
          density="comfortable"
        />
        <v-alert v-else-if="!spaceLoading" type="info" rounded="lg" variant="tonal" class="report-table-msg">
          لا توجد بيانات مساحات للمعايير المحددة.
        </v-alert>
      </v-window-item>
    </v-window>
  </v-container>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  ArcElement,
  Filler
} from 'chart.js';
import { Bar, Line, Doughnut } from 'vue-chartjs';

import {
  fetchRoomBookingsReport,
  fetchCourseReservationsReport,
  fetchSpaceBookingsReport
} from '../services/reportsService.js';
import { aggregateSpaceBookingsReport } from '../utils/spaceReportAggregate.js';
import spaceBookingService from '../services/spaceBookingService.js';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement, ArcElement, Filler);

const chartFont = () => ({
  font: {
    family: 'Cairo, sans-serif'
  }
});

export default {
  name: 'ReportsComponent',
  components: {
    Bar,
    Line,
    Doughnut,
    VueDatePicker
  },
  data() {
    return {
      activeTab: 'overview',
      refreshingAll: false,
      loadingRoom: false,
      courseLoading: false,
      spaceLoading: false,
      reportType: 'monthly',
      courseReportType: 'monthly',
      spaceReportType: 'monthly',
      reportTypes: [
        { text: 'شهري', value: 'monthly' },
        { text: 'ربع سنوي', value: 'quarterly' },
        { text: 'سنوي', value: 'yearly' }
      ],
      selectedRoom: null,
      selectedSpace: null,
      /** Raw numeric-capable arrays for charts */
      roomReportRaw: [],
      courseReportRaw: [],
      spaceReportRaw: [],
      /** 'server' if /admin/reports/space-bookings returned rows; otherwise client aggregate */
      spaceSource: 'aggregated',

      useCustomDates: false,
      customDateFrom: null,
      customDateTo: null,

      headers: {
        monthly: [
          { title: 'اسم القاعة', key: 'name', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'إجمالي قيمة الحجوزات', key: 'net_amount', align: 'center' },
          { title: 'إجمالي المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'الشهر', key: 'report_month', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ],
        quarterly: [
          { title: 'اسم القاعة', key: 'name', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'إجمالي قيمة الحجوزات', key: 'net_amount', align: 'center' },
          { title: 'إجمالي المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'الربع', key: 'report_quarter', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ],
        yearly: [
          { title: 'اسم القاعة', key: 'name', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'إجمالي قيمة الحجوزات', key: 'net_amount', align: 'center' },
          { title: 'إجمالي المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ]
      },
      courseHeaders: {
        monthly: [
          { title: 'عدد الدورات', key: 'count_courses', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'عدد المشاركين', key: 'number_of_people', align: 'center' },
          { title: 'إجمالي الخصومات', key: 'discount', align: 'center' },
          { title: 'إجمالي السعر', key: 'total_price', align: 'center' },
          { title: 'صافي المبلغ', key: 'net_amount', align: 'center' },
          { title: 'إجمالي المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'الشهر', key: 'report_month', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ],
        quarterly: [
          { title: 'عدد الدورات', key: 'count_courses', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'عدد المشاركين', key: 'number_of_people', align: 'center' },
          { title: 'إجمالي الخصومات', key: 'discount', align: 'center' },
          { title: 'إجمالي السعر', key: 'total_price', align: 'center' },
          { title: 'صافي المبلغ', key: 'net_amount', align: 'center' },
          { title: 'إجمالي المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'الربع', key: 'report_quarter', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ],
        yearly: [
          { title: 'عدد الدورات', key: 'count_courses', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'عدد المشاركين', key: 'number_of_people', align: 'center' },
          { title: 'إجمالي الخصومات', key: 'discount', align: 'center' },
          { title: 'إجمالي السعر', key: 'total_price', align: 'center' },
          { title: 'صافي المبلغ', key: 'net_amount', align: 'center' },
          { title: 'إجمالي المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ]
      },
      spaceHeaders: {
        monthly: [
          { title: 'المساحة', key: 'name', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'إجمالي القيمة', key: 'net_amount', align: 'center' },
          { title: 'المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'الشهر', key: 'report_month', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ],
        quarterly: [
          { title: 'المساحة', key: 'name', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'إجمالي القيمة', key: 'net_amount', align: 'center' },
          { title: 'المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'الربع', key: 'report_quarter', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ],
        yearly: [
          { title: 'المساحة', key: 'name', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'إجمالي القيمة', key: 'net_amount', align: 'center' },
          { title: 'المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'السنة', key: 'report_year', align: 'center' }
        ]
      }
    };
  },
  computed: {
    rooms() {
      return this.$store.state.room?.rooms || [];
    },
    spaces() {
      return this.$store.getters['space/allSpaces']?.length ? this.$store.getters['space/allSpaces'] : this.$store.state.space?.spaces || [];
    },

    doughnutOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '58%',
        plugins: {
          legend: { rtl: true, labels: chartFont(), position: 'bottom' },
          tooltip: chartFont()
        }
      };
    },
    verticalBarOpts() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false, ...chartFont() }, tooltip: chartFont() },
        scales: {
          x: {
            ticks: chartFont(),
            grid: { color: 'rgba(0,0,0,.06)' }
          },
          y: {
            beginAtZero: true,
            ticks: chartFont(),
            grid: { color: 'rgba(0,0,0,.06)' }
          }
        }
      };
    },
    horizontalBarOpts() {
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false, ...chartFont() }, tooltip: chartFont() },
        scales: {
          x: { beginAtZero: true, ticks: chartFont(), grid: { color: 'rgba(0,0,0,.06)' } },
          y: { ticks: chartFont(), grid: { display: false } }
        }
      };
    },
    verticalBarGroupedOpts() {
      const base = this.verticalBarOpts;
      return {
        ...base,
        plugins: {
          ...base.plugins,
          legend: { display: true, rtl: true, labels: chartFont(), position: 'bottom' },
          tooltip: base.plugins.tooltip
        }
      };
    },
    lineTrendOpts() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { rtl: true, labels: chartFont(), position: 'bottom' }, tooltip: chartFont() },
        elements: {
          point: {
            radius: 4,
            hoverRadius: 6
          },
          line: {
            tension: 0.25
          }
        },
        scales: {
          x: { ticks: chartFont(), grid: { color: 'rgba(0,0,0,.06)' } },
          y: { beginAtZero: true, ticks: chartFont(), grid: { color: 'rgba(0,0,0,.06)' } }
        }
      };
    },

    customRangeLabel() {
      if (!this.customDateFrom || !this.customDateTo) return '';
      return `${this.customDateFrom} — ${this.customDateTo}`;
    },
    /** Valid custom range: switch on, both dates, from ≤ to */
    customRangeActive() {
      if (!this.useCustomDates || !this.customDateFrom || !this.customDateTo) return false;
      const from = new Date(this.customDateFrom);
      const to = new Date(this.customDateTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;
      return from <= to;
    },

    filteredRoomRows() {
      if (!this.customRangeActive) return this.roomReportRaw;
      return this.roomReportRaw.filter(r => this.rowInCustomBounds(r));
    },
    filteredCourseRows() {
      if (!this.customRangeActive) return this.courseReportRaw;
      return this.courseReportRaw.filter(r => this.rowInCustomBounds(r));
    },
    filteredSpaceRows() {
      if (!this.customRangeActive) return this.spaceReportRaw;
      return this.spaceReportRaw.filter(r => this.rowInCustomBounds(r));
    },

    /** Tables & entity bar charts: summed by room / space / course name inside the range */
    displayRoomRows() {
      if (!this.customRangeActive) return this.roomReportRaw;
      return this.sumRowsByKey(this.filteredRoomRows, r => String(r.name ?? r.room_id ?? 'غير معروف'), [
        'count_booking',
        'net_amount',
        'sum_of_payments'
      ]);
    },
    displayCourseRowsRaw() {
      if (!this.customRangeActive) return this.courseReportRaw;
      const rows = this.filteredCourseRows;
      const hasNames = rows.some(r => ((r.name || r.course_name || '') + '').trim());
      return hasNames ? this.sumCourseRowsByCourseName(rows) : this.mergeCourseRowsTotal(rows);
    },
    displaySpaceRowsRaw() {
      if (!this.customRangeActive) return this.spaceReportRaw;
      return this.sumRowsByKey(this.filteredSpaceRows, r => String(r.name ?? r.study_space_id ?? 'غير معروف'), [
        'count_booking',
        'net_amount',
        'sum_of_payments'
      ]);
    },

    displayRoomRowsFormatted() {
      const rangeLbl = this.customRangeLabel;
      return this.displayRoomRows.map(r => ({
        ...r,
        custom_period_label: this.customRangeActive ? rangeLbl : '',
        net_amount: Number(r.net_amount || 0).toLocaleString('ar-LY'),
        sum_of_payments: Number(r.sum_of_payments || 0).toLocaleString('ar-LY'),
        report_month: this.customRangeActive
          ? ''
          : this.reportType === 'monthly'
            ? this.getMonthName(r.report_month)
            : r.report_month,
        report_quarter: this.customRangeActive
          ? ''
          : this.reportType === 'quarterly'
            ? this.getQuarterName(r.report_quarter)
            : r.report_quarter
      }));
    },

    getReportHeaders() {
      if (this.customRangeActive) {
        return [
          { title: 'اسم القاعة', key: 'name', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'إجمالي قيمة الحجوزات', key: 'net_amount', align: 'center' },
          { title: 'إجمالي المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'فترة التقرير', key: 'custom_period_label', align: 'center' }
        ];
      }
      return this.headers[this.reportType] || this.headers.monthly;
    },
    getCourseReportHeaders() {
      if (this.customRangeActive) {
        return [
          { title: 'البند / الدورة', key: 'name', align: 'center' },
          { title: 'عدد الدورات', key: 'count_courses', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'عدد المشاركين', key: 'number_of_people', align: 'center' },
          { title: 'إجمالي الخصومات', key: 'discount', align: 'center' },
          { title: 'إجمالي السعر', key: 'total_price', align: 'center' },
          { title: 'صافي المبلغ', key: 'net_amount', align: 'center' },
          { title: 'إجمالي المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'فترة التقرير', key: 'custom_period_label', align: 'center' }
        ];
      }
      return this.courseHeaders[this.courseReportType] || this.courseHeaders.monthly;
    },
    getSpaceReportHeaders() {
      if (this.customRangeActive) {
        return [
          { title: 'المساحة', key: 'name', align: 'center' },
          { title: 'عدد الحجوزات', key: 'count_booking', align: 'center' },
          { title: 'إجمالي القيمة', key: 'net_amount', align: 'center' },
          { title: 'المدفوعات', key: 'sum_of_payments', align: 'center' },
          { title: 'فترة التقرير', key: 'custom_period_label', align: 'center' }
        ];
      }
      return this.spaceHeaders[this.spaceReportType] || this.spaceHeaders.monthly;
    },

    displayCourseRowsFormatted() {
      return this.displayCourseRowsRaw.map(this.formatCourseNumericRowForTable);
    },

    displaySpaceRowsFormatted() {
      const rangeLbl = this.customRangeLabel;
      return this.displaySpaceRowsRaw.map(r => ({
        ...r,
        custom_period_label: this.customRangeActive ? rangeLbl : '',
        net_amount: Number(r.net_amount || 0).toLocaleString('ar-LY'),
        sum_of_payments: Number(r.sum_of_payments || 0).toLocaleString('ar-LY'),
        report_month: this.customRangeActive
          ? ''
          : this.spaceReportType === 'monthly'
            ? this.getMonthName(r.report_month)
            : r.report_month,
        report_quarter: this.customRangeActive
          ? ''
          : this.spaceReportType === 'quarterly'
            ? this.getQuarterName(r.report_quarter)
            : r.report_quarter
      }));
    },

    roomBarLabels() {
      const rows = this.displayRoomRows;
      if (!rows.length) return [];
      if (this.customRangeActive) return rows.map(r => r.name || String(r.room_id || '—'));
      return rows.map(r => this.periodLabel(r, this.reportType));
    },
    courseBarLabels() {
      const rows = this.displayCourseRowsRaw;
      if (!rows.length) return [];
      if (this.customRangeActive) {
        return rows.map(r => {
          const n = ((r.name || r.course_name || '') + '').trim();
          return n || this.customRangeLabel;
        });
      }
      return rows.map(r => this.periodLabel(r, this.courseReportType));
    },
    spaceBarLabels() {
      const rows = this.displaySpaceRowsRaw;
      if (!rows.length) return [];
      if (this.customRangeActive) return rows.map(r => r.name || String(r.study_space_id || '—'));
      return rows.map(r => this.periodLabel(r, this.spaceReportType));
    },

    roomBarChart() {
      const data = this.displayRoomRows.map(r => Number(r.net_amount) || 0);
      return this.barDataset('#1976d2', 'صافي القيمة', this.roomBarLabels, data);
    },
    roomLineChart() {
      const rows = this.customRangeActive
        ? this.sumRowsByPeriodGeneric(this.filteredRoomRows, this.reportType, [
            'count_booking',
            'net_amount',
            'sum_of_payments'
          ])
        : this.displayRoomRows;
      return {
        labels: rows.map(r => this.periodLabel(r, this.reportType)),
        datasets: [
          {
            label: 'عدد الحجوزات',
            data: rows.map(r => Number(r.count_booking) || 0),
            borderColor: '#00897b',
            backgroundColor: 'rgba(0,137,123,0.15)',
            fill: true,
            tension: 0.3,
            spanGaps: true
          }
        ]
      };
    },

    courseGroupedChart() {
      return {
        labels: this.courseBarLabels,
        datasets: [
          {
            label: 'عدد الحجوزات',
            data: this.displayCourseRowsRaw.map(r => Number(r.count_booking) || 0),
            backgroundColor: '#42a5f5',
            borderRadius: 8,
            maxBarThickness: 40
          },
          {
            label: 'عدد المشاركين',
            data: this.displayCourseRowsRaw.map(r => Number(r.number_of_people) || 0),
            backgroundColor: '#7e57c2',
            borderRadius: 8,
            maxBarThickness: 40
          }
        ]
      };
    },
    coursePriceTrend() {
      return {
        labels: this.courseBarLabels,
        datasets: [
          {
            label: 'إجمالي السعر',
            data: this.displayCourseRowsRaw.map(r => this.parseAmt(r.total_price)),
            borderColor: '#fbc02d',
            backgroundColor: 'rgba(251,192,45,0.12)',
            fill: true
          },
          {
            label: 'الخصم',
            data: this.displayCourseRowsRaw.map(r => this.parseAmt(r.discount)),
            borderColor: '#ef5350',
            tension: 0.3,
            fill: false
          }
        ]
      };
    },

    spaceBarChart() {
      const data = this.displaySpaceRowsRaw.map(r => Number(r.net_amount) || 0);
      return this.barDataset('#3949ab', 'صافي القيمة', this.spaceBarLabels, data);
    },

    spaceByPaymentTotal() {
      return this.displaySpaceRowsRaw.reduce((s, r) => s + (Number(r.sum_of_payments) || 0), 0);
    },
    spacePaymentDonut() {
      const paid = this.spaceByPaymentTotal;
      const net = this.displaySpaceRowsRaw.reduce((s, r) => s + (Number(r.net_amount) || 0), 0);
      const remaining = Math.max(net - paid, 0);
      return {
        labels: ['محصّل', 'متوقع متبقي'],
        datasets: [
          {
            data: [paid, remaining],
            backgroundColor: ['#43a047', '#bdbdbd']
          }
        ]
      };
    },

    totalsForKpi() {
      const rn = arr => arr.reduce((s, r) => s + (Number(r.net_amount) || 0), 0);
      const rc = arr => arr.reduce((s, r) => s + (Number(r.count_booking) || 0), 0);
      const rm = arr => arr.reduce((s, r) => s + (Number(r.sum_of_payments ?? r.sum_payments ?? 0) || 0), 0);

      const roomRows = this.filteredRoomRows;
      const courseRows = this.filteredCourseRows;
      const spaceRows = this.filteredSpaceRows;

      return {
        roomNet: rn(roomRows),
        roomCount: rc(roomRows),
        courseNet: rn(courseRows),
        courseCount: rc(courseRows),
        spaceNet: rn(spaceRows),
        spaceCount: rc(spaceRows),
        payments:
          rm(roomRows) +
          courseRows.reduce((s, r) => s + this.parseAmt(r.sum_of_payments), 0) +
          rm(spaceRows)
      };
    },

    kpiCards() {
      const t = this.totalsForKpi;
      return [
        {
          title: 'حجوزات القاعات (عدد)',
          value: t.roomCount.toLocaleString('ar-LY'),
          hint: `${t.roomNet.toLocaleString('ar-LY')} د.ل قيمة${this.customRangeActive ? ' — المدى المحدد' : ''}`,
          color: '#1976d2'
        },
        {
          title: 'حجوزات الدورات (عدد)',
          value: t.courseCount.toLocaleString('ar-LY'),
          hint: `${t.courseNet.toLocaleString('ar-LY')} د.ل صافي${this.customRangeActive ? ' — المدى المحدد' : ''}`,
          color: '#00897b'
        },
        {
          title: 'حجوزات المساحات (عدد)',
          value: t.spaceCount.toLocaleString('ar-LY'),
          hint: `${t.spaceNet.toLocaleString('ar-LY')} د.ل قيمة${this.customRangeActive ? ' — المدى المحدد' : ''}`,
          color: '#5e35b1'
        },
        {
          title: 'إجمالي المدفوعات',
          value: t.payments.toLocaleString('ar-LY'),
          hint: `دينار ليبي${this.customRangeActive ? ' — ضمن المدى المحدد' : ''} — بحسب التجميع المختار`,
          color: '#6d4c41'
        }
      ];
    },

    comparisonChartData() {
      const t = this.totalsForKpi;
      const data = [t.roomNet || 0, t.courseNet || 0, t.spaceNet || 0];
      return {
        labels: ['القاعات', 'الدورات', 'المساحات'],
        datasets: [
          {
            data,
            backgroundColor: ['rgba(25,118,210,0.85)', 'rgba(0,137,123,0.85)', 'rgba(94,53,177,0.85)'],
            hoverOffset: 8
          }
        ]
      };
    },

    paymentsBarData() {
      const rr = this.filteredRoomRows;
      const cr = this.filteredCourseRows;
      const sp = this.filteredSpaceRows;
      const r = rr.reduce((s, x) => s + (Number(x.sum_of_payments) || 0), 0);
      const c = cr.reduce((s, x) => s + this.parseAmt(x.sum_of_payments), 0);
      const spx = sp.reduce((s, x) => s + (Number(x.sum_of_payments) || 0), 0);
      return {
        labels: ['القاعات', 'الدورات', 'المساحات'],
        datasets: [
          {
            label: 'مدفوعات',
            data: [r, c, spx],
            backgroundColor: ['#26418f', '#00796b', '#4527a0']
          }
        ]
      };
    },

    trendMixedData() {
      const roomRows = this.customRangeActive
        ? this.sumRowsByPeriodGeneric(this.filteredRoomRows, this.reportType, [
            'count_booking',
            'net_amount',
            'sum_of_payments'
          ])
        : this.displayRoomRows;
      const courseRows = this.customRangeActive
        ? this.sumCourseRowsByPeriodFiltered()
        : this.displayCourseRowsRaw;
      const spaceRows = this.customRangeActive
        ? this.sumRowsByPeriodGeneric(this.filteredSpaceRows, this.spaceReportType, [
            'count_booking',
            'net_amount',
            'sum_of_payments'
          ])
        : this.displaySpaceRowsRaw;

      const labels = this.unionPeriodLabels([
        [roomRows, this.reportType],
        [courseRows, this.courseReportType],
        [spaceRows, this.spaceReportType]
      ]);
      const roomMap = new Map(roomRows.map(r => [this.periodKey(r, this.reportType), r]));
      const courseMap = new Map(courseRows.map(r => [this.periodKey(r, this.courseReportType), r]));
      const spaceMap = new Map(spaceRows.map(r => [this.periodKey(r, this.spaceReportType), r]));

      const pickCount = map => lbl => Number(map.get(lbl)?.count_booking) || 0;

      return {
        labels,
        datasets: [
          {
            label: 'القاعات',
            data: labels.map(pickCount(roomMap)),
            borderColor: '#1976d2',
            backgroundColor: 'rgba(25,118,210,0.08)',
            fill: true,
            tension: 0.3
          },
          {
            label: 'الدورات',
            data: labels.map(pickCount(courseMap)),
            borderColor: '#00897b',
            tension: 0.3
          },
          {
            label: 'المساحات',
            data: labels.map(pickCount(spaceMap)),
            borderColor: '#5e35b1',
            tension: 0.3
          }
        ]
      };
    }
  },

  methods: {
    unionPeriodLabels(pairs) {
      const lbls = new Set();
      for (const [rows, rt] of pairs) {
        (rows || []).forEach(r => lbls.add(this.periodLabelFlat(r, rt)));
      }
      return [...lbls].sort(this.sortFlatPeriod);
    },
    sortFlatPeriod(a, b) {
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    },

    parseAmt(v) {
      if (v === null || v === undefined || v === '') return 0;
      if (typeof v === 'number') return v;
      const cleaned = String(v).replace(/\s+/g, '').replace(/,/g, '');
      const n = parseFloat(cleaned);
      return Number.isNaN(n) ? 0 : n;
    },

    /** Sum numeric columns grouped by arbitrary key (e.g. room or space name) */
    sumRowsByKey(rows, keyFn, sumFields) {
      const map = new Map();
      for (const r of rows || []) {
        const k = keyFn(r);
        if (!map.has(k)) {
          const seed = { ...r };
          for (const f of sumFields) seed[f] = 0;
          map.set(k, seed);
        }
        const a = map.get(k);
        for (const f of sumFields) {
          a[f] = (Number(a[f]) || 0) + (Number(r[f]) || 0);
        }
      }
      return [...map.values()];
    },

    /** Merge rows per report period key (calendar month/quarter/year from row fields) */
    sumRowsByPeriodGeneric(rows, reportType, fields, moneyFields = []) {
      const money = moneyFields instanceof Array ? moneyFields : [];
      const map = new Map();
      const isMoney = f => money.includes(f);
      for (const r of rows || []) {
        const k = this.periodKey(r, reportType);
        if (!map.has(k)) {
          const seed = { ...r };
          for (const f of fields) seed[f] = 0;
          map.set(k, seed);
        }
        const a = map.get(k);
        for (const f of fields) {
          const inc = isMoney(f) ? this.parseAmt(r[f]) : Number(r[f]) || 0;
          a[f] = (Number(a[f]) || 0) + inc;
        }
      }
      return [...map.values()].sort((x, y) =>
        this.sortFlatPeriod(this.periodKey(x, reportType), this.periodKey(y, reportType))
      );
    },

    sumCourseRowsByPeriodFiltered() {
      return this.sumRowsByPeriodGeneric(
        this.filteredCourseRows,
        this.courseReportType,
        ['count_courses', 'count_booking', 'number_of_people', 'discount', 'total_price', 'net_amount', 'sum_of_payments'],
        ['discount', 'total_price', 'net_amount', 'sum_of_payments']
      );
    },

    sumCourseRowsByCourseName(rows) {
      const map = new Map();
      for (const r of rows || []) {
        const k = String(((r.name || r.course_name || '') + '').trim() || '—');
        if (!map.has(k)) {
          map.set(k, {
            ...r,
            name: k,
            course_name: k,
            count_courses: 0,
            count_booking: 0,
            number_of_people: 0,
            discount: 0,
            total_price: 0,
            net_amount: 0,
            sum_of_payments: 0
          });
        }
        const a = map.get(k);
        a.count_courses += Number(r.count_courses) || 0;
        a.count_booking += Number(r.count_booking) || 0;
        a.number_of_people += Number(r.number_of_people) || 0;
        a.discount += this.parseAmt(r.discount);
        a.total_price += this.parseAmt(r.total_price);
        a.net_amount += this.parseAmt(r.net_amount);
        a.sum_of_payments += this.parseAmt(r.sum_of_payments);
      }
      return [...map.values()];
    },

    mergeCourseRowsTotal(rows) {
      if (!rows || !rows.length) return [];
      const acc = {
        name: 'إجمالي الفترة',
        course_name: 'إجمالي الفترة',
        count_courses: 0,
        count_booking: 0,
        number_of_people: 0,
        discount: 0,
        total_price: 0,
        net_amount: 0,
        sum_of_payments: 0,
        report_month: null,
        report_quarter: null,
        report_year: null
      };
      for (const r of rows) {
        acc.count_courses += Number(r.count_courses) || 0;
        acc.count_booking += Number(r.count_booking) || 0;
        acc.number_of_people += Number(r.number_of_people) || 0;
        acc.discount += this.parseAmt(r.discount);
        acc.total_price += this.parseAmt(r.total_price);
        acc.net_amount += this.parseAmt(r.net_amount);
        acc.sum_of_payments += this.parseAmt(r.sum_of_payments);
      }
      return [acc];
    },

    syncCustomRangeWhenBothDates() {
      if (!this.customDateFrom || !this.customDateTo) return;
      const from = new Date(this.customDateFrom);
      const to = new Date(this.customDateTo);
      if (!Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime())) {
        this.useCustomDates = true;
      }
    },

    barDataset(color, label, labels, data) {
      return {
        labels,
        datasets: [
          {
            label,
            data,
            backgroundColor: color,
            borderRadius: 8,
            maxBarThickness: 48
          }
        ]
      };
    },

    periodLabel(row, type) {
      if (type === 'monthly') return `${this.getMonthName(row.report_month)} ${row.report_year}`;
      if (type === 'quarterly') return `${this.getQuarterName(row.report_quarter)} ${row.report_year}`;
      return String(row.report_year || '');
    },
    periodLabelFlat(row, type) {
      if (type === 'monthly') return `${row.report_year}-${String(row.report_month).padStart(2, '0')}`;
      if (type === 'quarterly') return `${row.report_year}-Q${row.report_quarter}`;
      return `${row.report_year}`;
    },
    periodKey(row, type) {
      return this.periodLabelFlat(row, type);
    },
    periodIndex(row, type) {
      return this.periodLabelFlat(row, type);
    },

    /** Fix period bounds for course/space tabs — use row own fields */
    rowInCustomBounds(row) {
      if (!this.useCustomDates || !this.customDateFrom || !this.customDateTo) return true;
      const from = new Date(this.customDateFrom);
      const to = new Date(this.customDateTo);
      to.setHours(23, 59, 59, 999);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return true;
      const y = Number(row.report_year);
      if (!y) return true;
      let start;
      let end;
      if (row.report_month != null) {
        const m = Number(row.report_month) - 1;
        start = new Date(y, m, 1);
        end = new Date(y, m + 1, 0, 23, 59, 59);
      } else if (row.report_quarter != null) {
        const q = Number(row.report_quarter);
        const m0 = (q - 1) * 3;
        start = new Date(y, m0, 1);
        end = new Date(y, m0 + 3, 0, 23, 59, 59);
      } else {
        start = new Date(y, 0, 1);
        end = new Date(y, 11, 31, 23, 59, 59);
      }
      return end >= from && start <= to;
    },

    formatCourseNumericRowForTable(item) {
      const displayName = (item.name || item.course_name || '').toString().trim();
      return {
        ...item,
        name: displayName || item.name || item.course_name || '—',
        custom_period_label: this.customRangeActive ? this.customRangeLabel : '',
        discount: this.formatNumber(this.parseAmt(item.discount)),
        total_price: this.formatNumber(this.parseAmt(item.total_price)),
        net_amount: this.formatNumber(this.parseAmt(item.net_amount)),
        sum_of_payments: this.formatNumber(this.parseAmt(item.sum_of_payments)),
        report_month: this.customRangeActive
          ? ''
          : this.courseReportType === 'monthly'
            ? this.getMonthName(item.report_month)
            : item.report_month,
        report_quarter: this.customRangeActive
          ? ''
          : this.courseReportType === 'quarterly'
            ? this.getQuarterName(item.report_quarter)
            : item.report_quarter
      };
    },

    formatNumber(n) {
      if (n === null || n === undefined) return '';
      return Number(n).toLocaleString('ar-LY');
    },

    getMonthName(monthNumber) {
      const months = [
        'يناير',
        'فبراير',
        'مارس',
        'أبريل',
        'مايو',
        'يونيو',
        'يوليو',
        'أغسطس',
        'سبتمبر',
        'أكتوبر',
        'نوفمبر',
        'ديسمبر'
      ];
      return months[parseInt(monthNumber, 10) - 1] || monthNumber;
    },
    getQuarterName(quarter) {
      const quarters = {
        1: 'الربع الأول',
        2: 'الربع الثاني',
        3: 'الربع الثالث',
        4: 'الربع الرابع'
      };
      return quarters[quarter] || quarter;
    },

    async generateReport() {
      if (this.loadingRoom) return;
      this.loadingRoom = true;
      try {
        const params = {
          room_id: this.selectedRoom?.id,
          report_type: this.reportType
        };
        const data = await fetchRoomBookingsReport(params);
        this.roomReportRaw = Array.isArray(data) ? data.map(this.normalizeRoomRow) : [];
        this.$router.replace({
          query: {
            ...this.$route.query,
            room_id: this.selectedRoom?.id,
            report_type: this.reportType
          }
        });
      } catch (e) {
        console.error(e);
        this.$store.dispatch('showAlert', { message: 'تعذر تحميل تقرير القاعات', type: 'error' });
      } finally {
        this.loadingRoom = false;
      }
    },
    normalizeRoomRow(item) {
      return {
        ...item,
        count_booking: Number(item.count_booking) || 0,
        net_amount: Number(item.net_amount) || 0,
        sum_of_payments: Number(item.sum_of_payments) || 0
      };
    },

    async generateCourseReport() {
      this.courseLoading = true;
      try {
        const raw = await fetchCourseReservationsReport(this.courseReportType);
        this.courseReportRaw = Array.isArray(raw)
          ? raw.map(r => ({
              ...r,
              count_courses: Number(r.count_courses) || 0,
              count_booking: Number(r.count_booking) || 0,
              number_of_people: Number(r.number_of_people) || 0,
              discount: this.parseAmt(r.discount),
              total_price: this.parseAmt(r.total_price),
              net_amount: this.parseAmt(r.net_amount),
              sum_of_payments: this.parseAmt(r.sum_of_payments)
            }))
          : [];
      } catch (e) {
        console.error(e);
        this.$store.dispatch('showAlert', { message: 'تعذر تحميل تقرير الدورات', type: 'error' });
      } finally {
        this.courseLoading = false;
      }
    },

    async generateSpaceReport() {
      this.spaceLoading = true;
      try {
        const params = {
          report_type: this.spaceReportType,
          study_space_id: this.selectedSpace?.id,
          space_id: this.selectedSpace?.id
        };
        let server = await fetchSpaceBookingsReport(params);
        if (server && server.length) {
          this.spaceReportRaw = server.map(this.normalizeSpaceRow);
          this.spaceSource = 'server';
        } else {
          const res = await spaceBookingService.getSpaceBookings({});
          const raw = this.extractBookingList(res);
          this.spaceReportRaw = aggregateSpaceBookingsReport(
            raw,
            this.spaceReportType,
            this.selectedSpace || null
          ).map(this.normalizeSpaceRow);
          this.spaceSource = 'aggregated';
        }
      } catch (e) {
        console.error(e);
        this.$store.dispatch('showAlert', { message: 'تعذر تحميل تقرير المساحات', type: 'error' });
        this.spaceReportRaw = [];
      } finally {
        this.spaceLoading = false;
      }
    },
    normalizeSpaceRow(item) {
      return {
        ...item,
        count_booking: Number(item.count_booking) || 0,
        net_amount: Number(item.net_amount) || 0,
        sum_of_payments: Number(item.sum_of_payments) || 0
      };
    },

    async refreshAll() {
      this.refreshingAll = true;
      await Promise.all([this.generateReport(), this.generateCourseReport(), this.generateSpaceReport()]);
      this.refreshingAll = false;
    },

    csvEscape(v) {
      const s = v == null ? '' : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    },

    rowsToCsv(headers, rows) {
      const head = headers.map(h => this.csvEscape(h.title)).join(',');
      const lines = rows.map(row =>
        headers
          .map(h => this.csvEscape(row[h.key]))
          .join(',')
      );
      return [head, ...lines].join('\n');
    },

    exportCsvCurrentTab() {
      let headers;
      let rows;
      if (this.activeTab === 'rooms' || this.activeTab === 'overview') {
        headers = this.getReportHeaders;
        rows =
          this.activeTab === 'overview'
            ? this.displayRoomRowsFormatted
            : this.displayRoomRowsFormatted;
      }
      if (this.activeTab === 'courses') {
        headers = this.getCourseReportHeaders;
        rows = this.displayCourseRowsFormatted;
      }
      if (this.activeTab === 'spaces') {
        headers = this.getSpaceReportHeaders;
        rows = this.displaySpaceRowsFormatted;
      }
      if (!headers?.length || !rows?.length) {
        this.$store.dispatch('showAlert', { message: 'لا توجد صفوف للتصدير — حدّث التقرير أولاً', type: 'info' });
        return;
      }
      const BOM = '\uFEFF';
      const csv = BOM + this.rowsToCsv(headers, rows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix =
        this.customRangeActive ? `_${this.customDateFrom}_${this.customDateTo}` : '';
      a.download = `report-${this.activeTab}${suffix}-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },

    extractBookingList(payload) {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      const d = payload.data;
      if (Array.isArray(d)) return d;
      if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data;
      return [];
    }
  },

  async created() {
    await Promise.all([
      this.$store.dispatch('fetchRooms'),
      this.$store.dispatch('space/fetchSpaces').catch(() => {})
    ]);

    const roomId = this.$route.query.room_id;
    const roomsList = this.rooms || [];
    if (roomId && roomsList.find(r => String(r.id) === String(roomId))) {
      this.selectedRoom = roomsList.find(r => String(r.id) === String(roomId));
    }

    const reportType = this.$route.query.report_type;
    if (reportType && this.reportTypes.some(t => t.value === reportType)) {
      this.reportType = reportType;
      this.courseReportType = reportType;
      this.spaceReportType = reportType;
    }

    await this.refreshAll();
  },

  watch: {
    customDateFrom() {
      this.syncCustomRangeWhenBothDates();
    },
    customDateTo() {
      this.syncCustomRangeWhenBothDates();
    },
    activeTab(tab) {
      if (tab === 'courses' && !this.courseReportRaw.length) this.generateCourseReport();
      if (tab === 'spaces' && !this.spaceReportRaw.length) this.generateSpaceReport();
    }
  }
};
</script>

<style scoped>
.reports-page {
  direction: rtl;
  position: relative;
  z-index: 1;
  --report-pad: clamp(18px, 3.5vw, 26px);
  --report-section-gap: clamp(18px, 3vw, 26px);
  --report-chart-title-gap: 16px;
  --report-chart-h-default: 280px;
  --report-chart-h-tall: 300px;
  --report-chart-h-sm: 240px;
  --report-chart-h-wide: 320px;
}

.reports-hero {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: clamp(12px, 2.5vw, 20px);
}

.report-section {
  margin-bottom: var(--report-section-gap);
}

.report-card-pad {
  padding: var(--report-pad) !important;
}

.report-chart-heading {
  margin-bottom: var(--report-chart-title-gap) !important;
}

.report-chart-gap {
  margin-bottom: var(--report-section-gap);
}

.report-row-gap {
  margin-bottom: var(--report-section-gap);
}

.report-toolbar-note {
  margin-top: clamp(12px, 2vw, 18px);
}

.report-toolbar :deep(.v-row) {
  row-gap: 10px;
}

.report-hero-actions {
  gap: 12px;
}

.report-table-msg {
  margin-top: var(--report-section-gap);
}

.report-data-table {
  margin-top: var(--report-section-gap);
}

.overview-grid {
  row-gap: var(--report-section-gap);
}

.glass-card {
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(12px);
}

.border {
  border: 1px solid rgba(0, 0, 0, 0.06) !important;
}

.chart-card {
  min-height: 120px;
}

.chart-slot {
  height: var(--report-chart-h-default);
  position: relative;
}
.chart-slot-sm {
  height: var(--report-chart-h-sm);
  position: relative;
}
.chart-slot-tall {
  height: var(--report-chart-h-tall);
  position: relative;
}
.chart-slotwide,
.chart-slot-wide {
  height: var(--report-chart-h-wide);
  position: relative;
}

.chart-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(0, 0, 0, 0.47);
  font-size: 0.9375rem;
}

.kpi-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(250, 250, 252, 0.88));
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
}

.elevated-table {
  overflow: hidden;
}

.rtl-table {
  direction: rtl;
}

/* Date picker alignment with selects */
.ar-date :deep(.dp__input) {
  border-radius: 12px;
  text-align: right;
  direction: rtl;
  min-height: 48px;
  border-color: rgba(47, 48, 97, 0.15);
}

.ar-date :deep(.dp__input_wrap) {
  width: 100%;
}

.ar-date :deep(.dp__icon) {
  color: rgba(47, 48, 97, 0.65) !important;
}

.reports-page :deep(.hero-select.rtl-input) {
  margin-bottom: 0;
}

.report-tabs {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.report-tabs :deep(.v-slide-group__content) {
  gap: 2px;
}

.report-tabs :deep(.v-tab) {
  letter-spacing: 0;
  min-height: 48px !important;
  font-weight: 600;
}

.report-tabs :deep(.v-tab .v-icon),
.report-tabs :deep(.v-btn--stacked .v-icon) {
  color: inherit;
  opacity: 0.9;
}

.report-tabs :deep(.v-tab--selected .v-icon) {
  opacity: 1;
}
</style>
